import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { Footer } from '@/components/navigation/Footer';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { AIOnboardingModalFormShape } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import { useAIOnboardingModalContext } from '@/modules/aiOnboarding/AIOnboardingModal/context/AIOnboardingModal.context';
import { useDocumentChatContext } from '@/modules/chat/contexts/DocumentChatProvider';
import { ToggleChatButton } from '@/modules/documents/EditSummaryTrowser/components/ToggleChatButton';
import { useHouseholdChatReplacement } from '@/modules/household/chat/hooks/useHouseholdChatReplacement';

export function AIOnboardingModalFooter() {
  const [
    { isModalOpen: isConfirmCloseModalOpen },
    { setModalIsOpen: setConfirmCloseModalOpen },
  ] = useModalState();

  const { footerRef, onClose } = useAIOnboardingModalContext();

  const handleConfirmCloseModalClose = useCallback(
    (reason: 'cancel' | 'confirm') => {
      if (reason === 'confirm') {
        onClose();
      }
      setConfirmCloseModalOpen(false);
    },
    [onClose, setConfirmCloseModalOpen]
  );

  const { isChatOpen, openChat, closeChat } = useDocumentChatContext();
  const { isHouseholdChatReplacementEnabled } = useHouseholdChatReplacement();

  return (
    <>
      <Footer
        leftAction={
          !isHouseholdChatReplacementEnabled ? (
            <ToggleChatButton
              isChatOpen={isChatOpen}
              onOpenChat={openChat}
              onCloseChat={closeChat}
            />
          ) : undefined
        }
        rightAction={
          <Stack
            ref={footerRef}
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            direction="row"
          />
        }
      />
      <ConfirmationModal
        isOpen={isConfirmCloseModalOpen}
        heading="You have unsaved changes that will be discarded"
        cancelText="Cancel"
        confirmText="Proceed"
        confirmButtonProps={{ variant: 'primary' }}
        onClose={handleConfirmCloseModalClose}
      />
    </>
  );
}

export function AIOnboardingModalFooterActions({
  onSubmit,
  disabled,
  submitDisabled = false,
}: {
  onSubmit: () => void;
  disabled: boolean;
  submitDisabled?: boolean;
}) {
  const { onClose, suggestionsForSubmission, reviewedSuggestionIDs } =
    useAIOnboardingModalContext();

  const {
    formState: { isSubmitting },
  } = useFormContext<AIOnboardingModalFormShape>();

  // Confirm/Confirm all submits every pending suggestion in the modal's
  // scope (all pages), so label the button by that whole set, not just the
  // rows visible on the current page.
  const totalSuggestions = useMemo(() => {
    return suggestionsForSubmission.filter(
      (s) => !s.acceptanceStatus && !reviewedSuggestionIDs.includes(s.id)
    ).length;
  }, [suggestionsForSubmission, reviewedSuggestionIDs]);

  const buttonText = totalSuggestions === 1 ? 'Confirm' : 'Confirm all';

  return (
    <Stack spacing={1} direction="row">
      <Button
        variant="secondary"
        size="sm"
        disabled={disabled || isSubmitting}
        onClick={onClose}
      >
        Close
      </Button>
      <Button
        variant="primary"
        size="sm"
        type="submit"
        onClick={onSubmit}
        loading={isSubmitting}
        disabled={disabled || submitDisabled || isSubmitting}
      >
        {buttonText}
      </Button>
    </Stack>
  );
}
